import React, { HTMLAttributes } from 'react'

/**
 * A component that truncates text in different ways:
 * 1. Simple end truncation (default)
 * 2. Middle truncation (showing beginning and end of text)
 * 3. Multi-line truncation
 *
 * @example
 * // Basic end truncation
 * <Truncate text="Long text that will be truncated at the end..." />
 *
 * // Middle truncation
 * <Truncate text="Long text with middle truncation" middle={true} numberOfCharactersFromBack={5} />
 *
 * // Multi-line truncation
 * <Truncate text="Text that will be truncated after 2 lines" lines={2} />
 */
export interface TruncateProps extends HTMLAttributes<HTMLSpanElement> {
    /** The text to be truncated */
    text: string
    /** If true, text will be truncated in the middle, showing the beginning and end */
    middle?: boolean
    /** Number of characters to preserve from the end when using middle truncation */
    numberOfCharactersFromBack?: number
    /** Number of lines to show before truncating (for multi-line truncation) */
    lines?: number
}

export const Truncate = ({
    text,
    className,
    middle = false,
    numberOfCharactersFromBack = 10,
    lines = 1,
    ...props
}: TruncateProps): JSX.Element => {
    if (typeof text !== 'string') {
        return <>{text}</>
    }

    if (lines > 1)
        return (
            <span className={`line-clamp-${lines} max-w-full`} {...props}>
                {text}
            </span>
        )

    if (!middle) {
        return (
            <span className="truncate max-w-full" {...props}>
                {text}
            </span>
        )
    }

    // Ensure we don't take more characters than available, preventing text duplication
    const numberOfCharsToTake = Math.min(numberOfCharactersFromBack, text.length - 1)
    const firstPart = text.slice(0, text.length - numberOfCharsToTake)
    const secondPart = text.slice(-numberOfCharsToTake)

    //Check if we need to rescue a space character from disappearing into the html void
    const firstPartEndsWithSpace = firstPart.endsWith(' ')
    const secondPartStartsWithSpace = secondPart.startsWith(' ')
    const needsSpace = firstPartEndsWithSpace || secondPartStartsWithSpace

    return (
        <span className={`whitespace-nowrap overflow-hidden inline-flex max-w-full ${className ?? ''}`} {...props}>
            <span className="truncate">{firstPart}</span>
            {needsSpace && <span className="shrink-0">&nbsp;</span>}
            <span className="shrink-0">{secondPart}</span>
        </span>
    )
}
